import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  useArchiveDocument,
  useCreateStar,
  useDeleteDocument,
  useDeleteStar,
  useDocument,
  useDuplicateDocument,
  useMoveDocument,
  useRecordView,
  useStars,
  useTemplatizeDocument,
  useUpdateDocument,
} from "../api/documents";
import type { Doc, HeadingPrefix } from "../api/types";
import { useCollections } from "../api/queries";
import { granted } from "../auth/AbilityGate";
import { useAuth } from "../auth/auth-context";
import { ConnectionBanner } from "../collaboration/ConnectionBanner";
import { useCollaborationConfig } from "../collaboration/config";
import { PresenceAvatars } from "../collaboration/PresenceAvatars";
import { useCollaboration } from "../collaboration/useCollaboration";
import type { CommentsView } from "../comments/CommentsPanel";
import type { CollaborationOptions } from "../editor/collab";
import type { CommentAnchorSelection, EditorHandle } from "../editor/types";
import { TitleInput } from "../editor/TitleInput";
import { PageHeader } from "../layout/PageHeader";
import { cx } from "../lib/cx";
import { isEditableTarget } from "../lib/platform";
import { Button } from "../ui/Button";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Dialog } from "../ui/Dialog";
import { Menu, type MenuItem } from "../ui/Menu";
import { Select } from "../ui/Select";
import { useToast } from "../ui/toast-context";
import { Backlinks } from "./Backlinks";
import { DocumentBreadcrumb } from "./Breadcrumb";
import { DestinationDialog } from "./DestinationDialog";
import { ShareDialog } from "./ShareDialog";
import { TableOfContentsPanel } from "./TableOfContentsPanel";
import {
  createDocumentFromMention,
  exportDocumentMarkdown,
  resolveDocumentUrl,
  searchMentions,
  uploadAttachment,
} from "./editor-bridge";
import type { OutlineEntry, TaskSummary } from "./outline";
import { useDocumentSession } from "./session";

/** The editing surface, and everything it drags in, in a chunk of its own. */
const DocumentEditor = lazy(() => import("./DocumentEditor"));

/**
 * The sidebar carries a composer, which is a second editor, so it is lazy for
 * the same reason: a reader who never opens comments never downloads it.
 */
const CommentsPanel = lazy(() =>
  import("../comments/CommentsPanel").then((module) => ({ default: module.CommentsPanel })),
);

const HEADING_PREFIXES: { value: HeadingPrefix; label: string }[] = [
  { value: "none", label: "None" },
  { value: "numeric", label: "1. 1.1. 1.1.1." },
  { value: "alphanumeric", label: "A. 1. a. i." },
  { value: "outline", label: "I. A. 1. a." },
];

function statusLabel(status: string, savedAt: number | null): string {
  switch (status) {
    case "pending":
      return "Unsaved changes";
    case "saving":
      return "Saving…";
    case "saved":
      return savedAt === null ? "Saved" : "Saved";
    case "conflict":
      return "Reloaded";
    case "error":
      return "Could not save";
    default:
      return "";
  }
}

/** The live session persists the body itself; nothing here has to be scheduled. */
function noop(): void {
  /* the CRDT is the save path */
}

function badgeFor(document: Doc): string | null {
  if (document.deletedAt !== null) return "Deleted";
  if (document.archivedAt !== null) return "Archived";
  if (document.template === true) return "Template";
  if (document.publishedAt === null) return "Draft";
  return null;
}

export interface DocumentBodyProps {
  document: Doc;
  abilities: Record<string, boolean>;
  mode: "read" | "edit";
  /** Re-read the document from the server and start a fresh editing session. */
  onReload: () => void;
}

/**
 * The document screen proper. It is mounted under a key that changes when the
 * document changes or a conflict forces a re-read, so the editing session and
 * the editor's own contents start clean rather than being reconciled in place.
 */
function DocumentBody({ document, abilities, mode, onReload }: DocumentBodyProps): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { session: auth } = useAuth();

  const [outline, setOutline] = useState<OutlineEntry[]>([]);
  const [tasks, setTasks] = useState<TaskSummary>({ completed: 0, total: 0 });
  const [tocOpen, setTocOpen] = useState(false);
  // §15: `?commentId=` opens the sidebar on that thread and `?resolved` opens it
  // in the resolved view; both are read once and then dropped from the address.
  const deepLinkComment = searchParams.get("commentId");
  const [commentsOpen, setCommentsOpen] = useState(
    deepLinkComment !== null || searchParams.has("resolved"),
  );
  const [shareOpen, setShareOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [numberingOpen, setNumberingOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [followingId, setFollowingId] = useState<string | null>(null);
  const [commentsView, setCommentsView] = useState<CommentsView>(() =>
    searchParams.has("resolved") ? "resolved" : "unresolved",
  );
  const [focusedCommentId, setFocusedCommentId] = useState<string | null>(null);
  const [draftRequest, setDraftRequest] = useState<{
    nonce: number;
    selection: CommentAnchorSelection;
  } | null>(null);
  const handleRef = useRef<EditorHandle | null>(null);

  // The body handed to the editor is captured once: a save answers with the
  // text it was given, and feeding that back would fight whatever has been
  // typed since. A reload remounts this component, which re-captures it.
  const [baseline] = useState(() => ({
    text: document.text ?? "",
    revision: document.revision,
    title: document.title,
  }));

  const update = useUpdateDocument();
  const archive = useArchiveDocument();
  const remove = useDeleteDocument();
  const duplicate = useDuplicateDocument();
  const templatize = useTemplatizeDocument();
  const move = useMoveDocument();
  const createStar = useCreateStar();
  const deleteStar = useDeleteStar();
  const stars = useStars();
  const collections = useCollections();
  const recordView = useRecordView();

  const star = (stars.data?.stars ?? []).find((entry) => entry.documentId === document.id);

  const preferences = auth?.info.user.preferences ?? {};
  const teamPreferences = auth?.info.team.preferences ?? {};
  const seamless = preferences.seamlessEdit !== false;
  const canUpdate = granted(abilities, "update");
  const editable = canUpdate && (seamless || mode === "edit") && document.deletedAt === null;
  const headingPrefix: HeadingPrefix = document.preferences?.headingPrefix ?? "none";

  const report = useCallback(
    (error: Error) => {
      toast.show(error.message, "error");
    },
    [toast],
  );

  const onConflict = useCallback(() => {
    toast.show("This document changed elsewhere, so it was reloaded.", "error");
    onReload();
  }, [onReload, toast]);

  /*
   * Live collaboration (`docs/spec/collaboration.md`). Anyone who may update the
   * document joins the session; everyone else — a reader, a trashed document, a
   * screen where the transport is switched off — gets the non-realtime editor of
   * §16, which saves through `documents.update`.
   */
  const collaborationConfig = useCollaborationConfig();
  const userId = auth?.info.user.id ?? "";
  const userName = auth?.info.user.name ?? "";
  const collaborative =
    canUpdate && document.deletedAt === null && collaborationConfig.enabled !== false;
  const collaboration = useCollaboration({
    documentId: collaborative ? document.id : null,
    user: collaborative && userId !== "" ? { id: userId, name: userName } : null,
    enabled: collaborative,
    ...(collaborationConfig.connector === undefined
      ? {}
      : { connector: collaborationConfig.connector }),
    ...(collaborationConfig.localStore === undefined
      ? {}
      : { localStore: collaborationConfig.localStore }),
  });

  const { ydoc, provider, user: identity } = collaboration;
  const liveOptions: CollaborationOptions | undefined = useMemo(
    () =>
      ydoc !== null && provider !== null && identity !== null
        ? { ydoc, provider, user: identity }
        : undefined,
    [identity, provider, ydoc],
  );
  const live = liveOptions !== undefined;

  // The body is only read out of the editor when this session is the one saving
  // it. Under collaboration the CRDT owns the body and this path carries the
  // title alone, so it reads nothing and sends no `text`.
  const readMarkdown = useCallback(
    () => (live ? null : (handleRef.current?.getMarkdown() ?? null)),
    [live],
  );

  const editor = useDocumentSession({
    documentId: document.id,
    revision: baseline.revision,
    initialTitle: baseline.title,
    readOnly: !editable,
    readMarkdown,
    sendLastRevision: !live,
    onConflict,
    onError: report,
  });

  // While the socket is still being opened the body is not yet the shared one,
  // so input is refused rather than typed into a replica about to be replaced.
  const bodyReadOnly = !editable || (collaborative && (!live || !collaboration.editable));

  // §10: follow mode is derived, never stored — someone who disconnects simply
  // stops appearing here, and following goes inert with no state to unwind.
  const followed =
    followingId === null
      ? null
      : (collaboration.peers.find((peer) => peer.id === followingId) ?? null);
  const followedScrollY = followed?.scrollY ?? null;
  useEffect(() => {
    if (followedScrollY !== null) {
      window.scrollTo({ top: followedScrollY * window.innerHeight, behavior: "smooth" });
    }
  }, [followedScrollY]);

  const publishScroll = collaboration.publishScroll;
  useEffect(() => {
    if (!live) return;
    const onScroll = (): void => {
      publishScroll(window.scrollY / Math.max(window.innerHeight, 1));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [live, publishScroll]);

  // `views.create` records the read (note 03 §4). One call per document per
  // visit; the endpoint is idempotent per user and document.
  const recordViewMutate = recordView.mutate;
  useEffect(() => {
    recordViewMutate({ documentId: document.id });
  }, [document.id, recordViewMutate]);

  // The canonical URL carries the current title; arriving on a stale slug
  // rewrites the address bar rather than redirecting.
  useEffect(() => {
    const suffix = mode === "edit" ? "/edit" : "";
    const canonical = `${document.url}${suffix}`;
    if (location.pathname !== canonical) {
      void navigate(canonical, { replace: true });
    }
  }, [document, location.pathname, mode, navigate]);

  const documentId = document.id;
  const collectionId = document.collectionId;

  const onUpload = useCallback((file: File) => uploadAttachment(file, documentId), [documentId]);
  const onCreateDocument = useCallback(
    (title: string) =>
      createDocumentFromMention(title, {
        collectionId: collectionId ?? null,
        parentDocumentId: documentId ?? null,
      }),
    [collectionId, documentId],
  );
  const onOpenLink = useCallback(
    (href: string) => {
      if (href.startsWith("/")) void navigate(href);
      else window.open(href, "_blank", "noopener,noreferrer");
    },
    [navigate],
  );
  const onNotice = useCallback(
    (message: string) => {
      toast.show(message);
    },
    [toast],
  );

  // The collection carries its own switches for sharing and commenting; both are
  // ANDed under the workspace's (`docs/spec/shares.md` §2.5, `comments.md` §12).
  // A document with no collection — a draft — leaves the workspace deciding.
  const collection = (collections.data ?? []).find((entry) => entry.id === document.collectionId);
  const sharingEnabled = auth?.info.team.sharing !== false && collection?.sharing !== false;

  const canComment = granted(abilities, "comment");
  // A guest in a workspace that allows members to comment may read threads
  // without adding to them (§12); only a workspace or collection that has turned
  // commenting off hides the sidebar entirely.
  const commentingEnabled =
    teamPreferences.commenting !== "none" && collection?.commenting !== false;

  /** `Mod-Alt-M` and the selection toolbar's Comment button (§15). */
  const onCreateComment = useCallback(
    (selection: CommentAnchorSelection) => {
      if (!canComment) return;
      setCommentsOpen(true);
      if (selection.empty) return;
      setDraftRequest({ nonce: Date.now(), selection });
    },
    [canComment],
  );

  const onCommentClick = useCallback(
    (commentId: string) => {
      if (!commentingEnabled) return;
      setCommentsOpen(true);
      setFocusedCommentId(commentId);
    },
    [commentingEnabled],
  );

  const clearDeepLink = useCallback(() => {
    if (!searchParams.has("commentId") && !searchParams.has("resolved")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("commentId");
    next.delete("resolved");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Document-scoped keys (note 03 §10): edit, move, history, and the outline.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || isEditableTarget(event.target)) return;
      if ((event.metaKey || event.ctrlKey) && event.altKey && event.key.toLowerCase() === "m") {
        event.preventDefault();
        setCommentsOpen((value) => !value);
        return;
      }
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === "h") {
        event.preventDefault();
        setTocOpen((value) => !value);
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === "e") {
        event.preventDefault();
        void navigate(`${document.url}/edit`);
      } else if (key === "m" && granted(abilities, "move")) {
        event.preventDefault();
        setMoveOpen(true);
      } else if (key === "h") {
        event.preventDefault();
        void navigate(`${document.url}/history`);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [abilities, document, navigate]);

  const overflow = useMemo((): MenuItem[] => {
    const items: MenuItem[] = [];
    if (granted(abilities, "duplicate")) {
      items.push({
        label: "Duplicate",
        onSelect: () => {
          duplicate.mutate(
            { id: document.id },
            {
              onSuccess: (copy) => {
                toast.show("Document duplicated");
                void navigate(copy.url);
              },
              onError: report,
            },
          );
        },
      });
    }
    if (granted(abilities, "move")) {
      items.push({
        label: "Move…",
        onSelect: () => {
          setMoveOpen(true);
        },
      });
    }
    if (canUpdate && document.template !== true) {
      items.push({
        label: "Templatize",
        onSelect: () => {
          templatize.mutate(
            { id: document.id, publish: true },
            {
              onSuccess: (template) => {
                toast.show("Template created");
                void navigate(template.url);
              },
              onError: report,
            },
          );
        },
      });
    }
    items.push(
      star === undefined
        ? {
            label: "Star",
            disabled: !granted(abilities, "star"),
            onSelect: () => {
              createStar.mutate({ documentId: document.id }, { onError: report });
            },
          }
        : {
            label: "Unstar",
            onSelect: () => {
              deleteStar.mutate({ id: star.id }, { onError: report });
            },
          },
    );
    if (granted(abilities, "download")) {
      items.push({
        label: "Export markdown",
        onSelect: () => {
          void exportDocumentMarkdown(document.id, document.title).catch(report);
        },
      });
    }
    items.push({
      label: "Print",
      onSelect: () => {
        window.print();
      },
    });
    if (canUpdate) {
      items.push({
        label: document.fullWidth === true ? "Use fixed width" : "Use full width",
        onSelect: () => {
          update.mutate(
            { id: document.id, fullWidth: document.fullWidth !== true },
            { onError: report },
          );
        },
      });
      items.push({
        label: "Heading numbering…",
        onSelect: () => {
          setNumberingOpen(true);
        },
      });
    }
    if (granted(abilities, "archive")) {
      items.push({
        label: "Archive",
        onSelect: () => {
          archive.mutate(
            { id: document.id },
            {
              onSuccess: () => {
                toast.show("Document archived");
              },
              onError: report,
            },
          );
        },
      });
    }
    if (granted(abilities, "delete")) {
      items.push({
        label: "Delete",
        destructive: true,
        onSelect: () => {
          setDeleteOpen(true);
        },
      });
    }
    return items;
  }, [
    abilities,
    archive,
    canUpdate,
    createStar,
    deleteStar,
    document,
    duplicate,
    navigate,
    report,
    star,
    templatize,
    toast,
    update,
  ]);

  const badge = badgeFor(document);
  const tocPosition = teamPreferences.tocPosition ?? "right";

  return (
    <>
      <PageHeader>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <DocumentBreadcrumb document={document} />
          {badge !== null && (
            <span className="rounded border border-line px-1 py-px text-xs text-muted">
              {badge}
            </span>
          )}
          {tasks.total > 0 && (
            <span className="text-xs text-muted">
              {tasks.completed}/{tasks.total} tasks
            </span>
          )}
          {editor.status !== "idle" &&
            (!live || editor.status === "conflict" || editor.status === "error") && (
              <span role="status" className="text-xs text-muted">
                {statusLabel(editor.status, editor.savedAt)}
              </span>
            )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <PresenceAvatars
            peers={collaboration.peers}
            followingId={followed?.id ?? null}
            onFollow={(peer) => {
              setFollowingId(peer?.id ?? null);
            }}
            className="mr-2"
          />
          {document.publishedAt === null && granted(abilities, "publish") && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setPublishOpen(true);
              }}
            >
              Publish
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => {
              setShareOpen(true);
            }}
          >
            Share
          </Button>
          {commentingEnabled && (
            <Button
              size="sm"
              aria-pressed={commentsOpen}
              onClick={() => {
                setCommentsOpen((value) => !value);
              }}
            >
              Comments
            </Button>
          )}
          <Link
            to={`${document.url}/history`}
            className="rounded-md px-2 py-1 text-sm text-muted hover:bg-hover hover:text-ink"
          >
            History
          </Link>
          <Button
            size="sm"
            aria-pressed={tocOpen}
            onClick={() => {
              setTocOpen((value) => !value);
            }}
          >
            Contents
          </Button>
          <Menu
            buttonLabel="Document actions"
            menuLabel="Document actions"
            items={overflow}
            trigger="⋯"
          />
        </div>
      </PageHeader>

      <div className="flex gap-6">
        {tocOpen && (
          <TableOfContentsPanel
            entries={outline}
            position={tocPosition}
            onClose={() => {
              setTocOpen(false);
            }}
          />
        )}

        <article className={cx("min-w-0 flex-1", document.fullWidth !== true && "max-w-3xl")}>
          {collaborative && (
            <ConnectionBanner
              status={collaboration.status}
              hasLocalPersistence={collaboration.hasLocalPersistence}
              className="mb-3"
            />
          )}
          {/* Leaving the title or the body flushes the pending save with
              `done: true`, which is what asks the server for a snapshot. */}
          <div
            onBlur={() => {
              editor.finish();
            }}
          >
            <TitleInput
              value={editor.title}
              onChange={editor.setTitle}
              readOnly={!editable}
              fullWidth={document.fullWidth === true}
              onEnter={() => {
                handleRef.current?.focus();
              }}
            />
            <Suspense
              fallback={
                <p role="status" className="text-sm text-muted">
                  Loading editor…
                </p>
              }
            >
              <DocumentEditor
                value={baseline.text}
                readOnly={bodyReadOnly}
                {...(liveOptions === undefined ? {} : { collaboration: liveOptions })}
                fullWidth={document.fullWidth === true}
                documentId={document.id}
                userId={auth?.info.user.id ?? ""}
                template={document.template === true}
                lineNumbers={preferences.codeBlockLineNumbers === true}
                headingPrefix={headingPrefix}
                handleRef={handleRef}
                onChange={live ? noop : editor.noteChange}
                onOutline={setOutline}
                onTasks={setTasks}
                onUpload={onUpload}
                onSearchMentions={searchMentions}
                onCreateDocument={onCreateDocument}
                onResolveDocumentUrl={resolveDocumentUrl}
                onOpenLink={onOpenLink}
                onNotice={onNotice}
                commentsInGutter={preferences.commentsInGutter === true}
                onCommentClick={onCommentClick}
                onCreateComment={onCreateComment}
              />
            </Suspense>
          </div>

          <Backlinks documentId={document.id} />
        </article>

        {commentsOpen && commentingEnabled && (
          <Suspense
            fallback={
              <div className="w-80 shrink-0 border-l border-line pl-4">
                <p role="status" className="text-sm text-muted">
                  Loading comments…
                </p>
              </div>
            }
          >
            <CommentsPanel
              documentId={document.id}
              userId={auth?.info.user.id ?? ""}
              canComment={canComment}
              view={commentsView}
              onViewChange={setCommentsView}
              sortByOrderInDocument={preferences.sortCommentsByOrderInDocument === true}
              editorRef={handleRef}
              canWriteMarks={live && !bodyReadOnly}
              draftRequest={draftRequest}
              onDraftHandled={() => {
                setDraftRequest(null);
              }}
              deepLinkId={deepLinkComment}
              onDeepLinkHandled={clearDeepLink}
              focusedId={focusedCommentId}
              onFocusedChange={setFocusedCommentId}
              onSearchMentions={searchMentions}
              onClose={() => {
                setCommentsOpen(false);
              }}
            />
          </Suspense>
        )}
      </div>

      <ShareDialog
        open={shareOpen}
        title={document.title}
        target={{ documentId: document.id }}
        canShare={granted(abilities, "share")}
        sharingEnabled={sharingEnabled}
        canManageAccess={granted(abilities, "manageUsers")}
        onClose={() => {
          setShareOpen(false);
        }}
      />

      <DestinationDialog
        open={publishOpen}
        title="Publish document"
        description="Choose where this draft should live."
        confirmLabel="Publish"
        busy={update.isPending}
        initialCollectionId={document.collectionId ?? null}
        onClose={() => {
          setPublishOpen(false);
        }}
        onConfirm={(destination) => {
          update.mutate(
            {
              id: document.id,
              publish: true,
              collectionId: destination.collectionId,
            },
            {
              onSuccess: (published) => {
                setPublishOpen(false);
                toast.show("Document published");
                if (destination.parentDocumentId !== null) {
                  move.mutate(
                    {
                      id: published.id,
                      collectionId: destination.collectionId,
                      parentDocumentId: destination.parentDocumentId,
                    },
                    { onError: report },
                  );
                }
              },
              onError: report,
            },
          );
        }}
      />

      <DestinationDialog
        open={moveOpen}
        title="Move document"
        description={`Choose where “${document.title}” lives.`}
        confirmLabel="Move"
        busy={move.isPending}
        initialCollectionId={document.collectionId ?? null}
        excludeDocumentId={document.id}
        onClose={() => {
          setMoveOpen(false);
        }}
        onConfirm={(destination) => {
          move.mutate(
            {
              id: document.id,
              collectionId: destination.collectionId,
              parentDocumentId: destination.parentDocumentId,
            },
            {
              onSuccess: () => {
                setMoveOpen(false);
                toast.show("Document moved");
              },
              onError: report,
            },
          );
        }}
      />

      <Dialog
        open={numberingOpen}
        onClose={() => {
          setNumberingOpen(false);
        }}
        title="Heading numbering"
        width="sm"
      >
        <Select
          label="Style"
          value={headingPrefix}
          options={HEADING_PREFIXES}
          onChange={(value) => {
            update.mutate(
              { id: document.id, preferences: { headingPrefix: value } },
              { onError: report },
            );
          }}
        />
        <div className="mt-5 flex justify-end">
          <Button
            onClick={() => {
              setNumberingOpen(false);
            }}
          >
            Done
          </Button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        title="Delete document"
        description={`“${document.title}” moves to the trash, where it can still be restored.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => {
          setDeleteOpen(false);
        }}
        onConfirm={() => {
          remove.mutate(
            { id: document.id },
            {
              onSuccess: () => {
                setDeleteOpen(false);
                toast.show("Document moved to the trash");
                void navigate("/home");
              },
              onError: report,
            },
          );
        }}
      />
    </>
  );
}

/**
 * `/doc/:slug` and `/doc/:slug/edit`. This outer component owns the load: the
 * body is keyed on the slug and a reload counter, so a conflict re-read starts
 * a fresh editing session rather than patching a live one.
 */
export function DocumentScreen({ mode = "read" }: { mode?: "read" | "edit" }): ReactNode {
  const { slug } = useParams();
  const query = useDocument(slug);
  const [reload, setReload] = useState(0);

  const onReload = useCallback(() => {
    void query.refetch().then(() => {
      setReload((value) => value + 1);
    });
    // The query object is new on every render; its identity is the slug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted">
        Loading document…
      </p>
    );
  }

  if (query.isError || query.data === undefined) {
    return (
      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold">Document unavailable</h2>
        <p role="alert" className="mt-2 text-sm text-danger">
          {query.error?.message ?? "This document could not be loaded."}
        </p>
        <Link to="/home" className="mt-4 inline-block text-sm text-accent underline">
          Go home
        </Link>
      </div>
    );
  }

  return (
    <DocumentBody
      key={`${query.data.document.id}:${String(reload)}`}
      document={query.data.document}
      abilities={query.data.abilities}
      mode={mode}
      onReload={onReload}
    />
  );
}
